/**
 * Browsing and editing the autopilot's parameter set.
 *
 * Nothing is fetched on mount, deliberately. A refresh asks the aircraft to
 * stream its whole parameter set, which on a 57600 baud radio is minutes of
 * link the telemetry does not get; opening a panel must not cost an operator
 * their map. So the fetch is a button, and browsing the result is free.
 *
 * The completeness line is the point of this panel, not decoration. `complete`
 * always describes the whole fetch and never the search or the page: a
 * parameter that is absent because a radio dropped it looks exactly like one
 * the airframe does not have, and an operator who reads a filtered list as the
 * full set will conclude a parameter does not exist when it was simply lost.
 * That distinction is why the server reports at all rather than just returning
 * a list, and it is rendered above the list where it cannot be missed.
 *
 * Writes take tactical role, not operator. A parameter write changes how the
 * autopilot behaves when nobody is looking — arming checks, failsafe actions,
 * geofence enforcement all live here.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import {
  listParameters,
  refreshParameters,
  writeParameter,
  type ParameterPage,
  type ParameterRow,
} from '@/api/parameters';
import { useCanControl, useHasRole } from '@/stores/authStore';
import type { DroneId } from '@/types/domain';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
const MUTED = 'rgba(244,244,245,0.5)';

const PAGE = 100;

/**
 * MAV_PARAM_TYPE, named.
 *
 * The wire carries the enum number and the panel was printing it: a TYPE column
 * reading "9" tells an operator nothing, and it is the one column that decides
 * whether a value they are about to type will be truncated.
 */
const PARAM_TYPE: Record<number, string> = {
  1: 'uint8',
  2: 'int8',
  3: 'uint16',
  4: 'int16',
  5: 'uint32',
  6: 'int32',
  7: 'uint64',
  8: 'int64',
  9: 'float',
  10: 'double',
};

function typeName(raw: string | number): string {
  const asNumber = Number(raw);
  return PARAM_TYPE[asNumber] ?? String(raw);
}

export function ParameterBrowser({ droneId }: { droneId: DroneId }): ReactNode {
  const canFetch = useCanControl(droneId);
  const canWrite = useHasRole('tactical') && canFetch;
  const [page, setPage] = useState<ParameterPage | null>(null);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  // Values written since the last fetch, by name.
  //
  // The list comes from the cache the last FETCH filled, and a single write does
  // not update that cache — so without this the table went on showing the old
  // number directly beside a message saying the aircraft had accepted a new one.
  // The table is what an operator believes. These values are the newer of the
  // two and the server verified each against the vehicle's own echo, but they
  // did not come from the snapshot, so they are marked rather than blended in.
  const [written, setWritten] = useState<Record<string, number>>({});

  const load = useCallback(
    async (nextOffset: number, nextSearch: string) => {
      const result = await listParameters(droneId, {
        search: nextSearch,
        limit: PAGE,
        offset: nextOffset,
      });
      if (result.ok) {
        setPage(result.data);
        setError(null);
      } else {
        setPage(null);
        // 404 until a refresh has happened, which is a normal empty state
        // rather than a failure, so it is phrased as one.
        setError(
          result.error.kind === 'not_found'
            ? 'No parameters fetched from this aircraft yet.'
            : result.error.message,
        );
      }
    },
    [droneId],
  );

  useEffect(() => {
    setPage(null);
    setOffset(0);
    setSearch('');
    setNotice(null);
    setWritten({});
    void load(0, '');
  }, [droneId, load]);

  async function fetchAll() {
    setBusy(true);
    setNotice(null);
    const result = await refreshParameters(droneId);
    setBusy(false);
    if (!result.ok) {
      setError(result.error.message);
      return;
    }
    setNotice(result.data.message);
    setOffset(0);
    // A fresh snapshot supersedes every local write, so the overlay goes.
    setWritten({});
    await load(0, search);
  }

  async function commit(row: ParameterRow) {
    const value = Number(draft);
    if (!Number.isFinite(value)) {
      setError(`"${draft}" is not a number.`);
      return;
    }
    setBusy(true);
    const result = await writeParameter(droneId, row.name, value);
    setBusy(false);
    setEditing(null);
    if (!result.ok) {
      setError(result.error.message);
      return;
    }
    // Verified by the vehicle's own echo server-side, so an ok here means the
    // aircraft confirmed the new value rather than merely accepted the packet.
    setNotice(`${row.name}: ${result.data.result.message}`);
    if (result.data.result.ok) setWritten((prev) => ({ ...prev, [row.name]: value }));
    await load(offset, search);
  }

  const rows = page?.parameters ?? [];
  const shownTo = Math.min(offset + rows.length, page?.matched ?? 0);

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          placeholder="Filter by name, e.g. FS_"
          aria-label="Filter parameters"
          onChange={(e) => {
            setSearch(e.target.value);
            setOffset(0);
            void load(0, e.target.value);
          }}
          className="rounded-[8px] border bg-transparent px-2.5 py-1.5 text-[11px]"
          style={{ borderColor: 'rgba(255,255,255,0.16)', color: '#f4f4f5', width: 'min(200px, 100%)' }}
        />
        <button
          type="button"
          disabled={!canFetch || busy}
          onClick={() => void fetchAll()}
          className="rounded-[9px] border px-3 py-1.5 label-mono text-[10px] disabled:opacity-40"
          style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(244,244,245,0.85)' }}
          title="Asks the aircraft to stream every parameter. Minutes of link on a slow radio."
        >
          {busy ? 'FETCHING…' : 'FETCH FROM AIRCRAFT'}
        </button>
      </div>

      {page && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10.5px]">
          {/* A fetch that received nothing is not a complete set, whatever the
              completeness field says about it. No autopilot has zero
              parameters, so "COMPLETE SET" over an empty table reads as "this
              airframe has none" — which is a claim about the aircraft made out
              of an absence of evidence. */}
          <span
            className="rounded-[99px] px-2 py-0.5 label-mono text-[9px]"
            style={{
              color: page.received === 0 ? MUTED : page.complete ? OK : WARN,
              background:
                page.received === 0
                  ? 'rgba(255,255,255,0.07)'
                  : `color-mix(in oklch, ${page.complete ? OK : WARN} 16%, transparent)`,
            }}
          >
            {page.received === 0 ? 'NOTHING RECEIVED' : page.complete ? 'COMPLETE SET' : 'INCOMPLETE SET'}
          </span>
          <span style={{ color: MUTED }}>
            {page.received} received
            {page.expected === null ? '' : ` of ${page.expected} declared`}
            {page.missingCount > 0 ? ` · ${page.missingCount} never arrived` : ''}
          </span>
          {!page.complete && (
            <span style={{ color: WARN }}>
              A parameter missing from this list may have been dropped by the link rather than
              absent from the airframe. Fetch again before concluding anything.
            </span>
          )}
        </div>
      )}

      {notice && (
        <p className="text-[11px]" style={{ color: 'rgba(244,244,245,0.7)' }}>
          {notice}
        </p>
      )}
      {error && (
        <p className="text-[11px]" style={{ color: page ? DANGER : MUTED }}>
          {error}
        </p>
      )}

      {rows.length > 0 && (
        <>
          <div className="scroll-thin max-h-[320px] overflow-auto rounded-[10px]" style={{ background: 'rgba(0,0,0,0.28)' }}>
            <table className="w-full min-w-[420px] text-[11px]">
              <thead>
                <tr>
                  <th className="label-mono px-2.5 py-1.5 text-left text-[9px]" style={{ color: MUTED }}>
                    NAME
                  </th>
                  <th className="label-mono px-2.5 py-1.5 text-right text-[9px]" style={{ color: MUTED }}>
                    VALUE
                  </th>
                  <th className="label-mono px-2.5 py-1.5 text-left text-[9px]" style={{ color: MUTED }}>
                    TYPE
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.name} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <td className="label-mono px-2.5 py-1.5">{row.name}</td>
                    <td className="tnum px-2.5 py-1.5 text-right">
                      {editing === row.name ? (
                        <input
                          type="number"
                          value={draft}
                          autoFocus
                          aria-label={`New value for ${row.name}`}
                          onChange={(e) => setDraft(e.target.value)}
                          className="w-24 rounded-[6px] border bg-transparent px-1.5 py-0.5 text-right text-[11px]"
                          style={{ borderColor: 'rgba(255,255,255,0.25)', color: '#f4f4f5' }}
                        />
                      ) : written[row.name] !== undefined ? (
                        <span title="Written since the last fetch, confirmed by the aircraft. Not from the cached snapshot.">
                          {written[row.name]}
                          <span className="label-mono ml-1 text-[8px]" style={{ color: WARN }}>
                            SET
                          </span>
                        </span>
                      ) : (
                        row.value
                      )}
                    </td>
                    <td className="label-mono px-2.5 py-1.5 text-[9.5px]" style={{ color: MUTED }}>
                      {typeName(row.type)}
                    </td>
                    <td className="px-2.5 py-1.5 text-right">
                      {editing === row.name ? (
                        <span className="flex justify-end gap-1.5">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void commit(row)}
                            className="label-mono text-[9.5px] disabled:opacity-40"
                            style={{ color: OK }}
                          >
                            WRITE
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditing(null)}
                            className="label-mono text-[9.5px]"
                            style={{ color: MUTED }}
                          >
                            CANCEL
                          </button>
                        </span>
                      ) : (
                        canWrite && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditing(row.name);
                              setDraft(String(written[row.name] ?? row.value));
                            }}
                            className="label-mono text-[9.5px]"
                            style={{ color: 'rgba(244,244,245,0.6)' }}
                          >
                            EDIT
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-2 text-[10.5px]" style={{ color: MUTED }}>
            <span className="tnum">
              {offset + 1}–{shownTo} of {page?.matched ?? 0}
            </span>
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => {
                const next = Math.max(0, offset - PAGE);
                setOffset(next);
                void load(next, search);
              }}
              className="ml-auto label-mono text-[9.5px] disabled:opacity-30"
            >
              PREV
            </button>
            <button
              type="button"
              disabled={shownTo >= (page?.matched ?? 0)}
              onClick={() => {
                const next = offset + PAGE;
                setOffset(next);
                void load(next, search);
              }}
              className="label-mono text-[9.5px] disabled:opacity-30"
            >
              NEXT
            </button>
          </div>
        </>
      )}
    </div>
  );
}
